import { useEffect, useState } from "react";
import axios from "axios";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { toast } from "sonner";
import { UserCheck, Check, SkipForward, X } from "lucide-react";

/**
 * v3.10 — "Nomi da confermare".
 * Le schede private con nome e cognome ancora in un campo solo vengono proposte
 * una per volta, con la divisione suggerita dal codice fiscale.
 * Regola: il campo `name` (l'etichetta in elenco) viene ricostruito come "NOME COGNOME".
 */
export default function NameReviewDialog() {
  const [items, setItems] = useState([]);
  const [aperto, setAperto] = useState(false);
  const [inCorso, setInCorso] = useState(false);
  const [salvati, setSalvati] = useState(0);
  const [saltati, setSaltati] = useState(0);
  const [nome, setNome] = useState("");
  const [cognome, setCognome] = useState("");

  const carica = async () => {
    try {
      const r = await axios.get("/api/clients/name-review");
      setItems(r.data.items || []);
    } catch (e) {
      /* se la chiamata fallisce il riquadro non compare: nessun disturbo */
    }
  };

  useEffect(() => {
    carica();
  }, []);

  const corrente = items[0];

  useEffect(() => {
    if (aperto && corrente) {
      setNome((corrente.proposta && corrente.proposta.first_name) || "");
      setCognome((corrente.proposta && corrente.proposta.last_name) || "");
    }
  }, [aperto, corrente && corrente.client_id]);

  const conferma = async () => {
    const n = nome.trim().toUpperCase();
    const c = cognome.trim().toUpperCase();
    if (!n || !c) {
      toast.error("Servono sia il cognome sia il nome");
      return;
    }
    setInCorso(true);
    try {
      await axios.put(`/api/clients/${corrente.client_id}`, {
        first_name: n,
        last_name: c,
        name: `${n} ${c}`,
        name_split_source: "manuale",
      });
      setSalvati((s) => s + 1);
      setItems((prev) => prev.slice(1));
      toast.success("Scheda sistemata");
    } catch (e) {
      const d = e && e.response && e.response.data && e.response.data.detail;
      toast.error("Non salvato: " + (d || (e && e.message) || "errore"));
    } finally {
      setInCorso(false);
    }
  };

  const salta = () => {
    setItems((prev) => prev.slice(1));
    setSaltati((s) => s + 1);
  };

  const motivo = (x) => {
    if (!x) return "";
    if (x.punteggio === 3) return "Confermato dal codice fiscale";
    if (x.punteggio === 2) return "Il codice fiscale conferma il cognome, non il nome";
    if (x.punteggio === 1) return "Il codice fiscale conferma il nome, non il cognome";
    if (!x.fiscal_code) return "Nessun codice fiscale in scheda: decidi tu";
    return "Il codice fiscale non aiuta: controlla tu";
  };

  if (items.length === 0 && !aperto) return null;

  return (
    <>
      {items.length > 0 && !aperto && (
        <div
          data-testid="name-review-banner"
          className="flex flex-col sm:flex-row sm:items-center gap-3 rounded-lg border border-amber-300 bg-amber-50 dark:bg-amber-950/30 dark:border-amber-800 px-4 py-3"
        >
          <UserCheck className="h-5 w-5 text-amber-600 shrink-0" />
          <div className="flex-1 text-sm">
            <span className="font-medium">{items.length} schede</span> hanno ancora nome e cognome
            in un campo solo. Il codice fiscale può aiutarci a dividerle.
          </div>
          <Button size="sm" onClick={() => setAperto(true)} data-testid="name-review-open">
            Sistemale ora
          </Button>
        </div>
      )}

      <Dialog
        open={aperto}
        onOpenChange={(v) => {
          setAperto(v);
          if (!v) carica();
        }}
      >
        <DialogContent className="sm:max-w-[620px]" data-testid="name-review-dialog" aria-describedby={undefined}>
          <DialogHeader>
            <DialogTitle className="font-heading">Nome e cognome da confermare</DialogTitle>
          </DialogHeader>

          {corrente ? (
            <div className="space-y-4">
              <div className="text-sm text-muted-foreground">
                Ne restano <span className="font-medium">{items.length}</span>
                {salvati + saltati > 0 && (
                  <>
                    {" · "}sistemate {salvati}
                    {saltati > 0 && <> · saltate {saltati}</>}
                  </>
                )}
              </div>

              <div className="rounded-md border bg-muted/40 px-3 py-2 text-sm space-y-1">
                <div>
                  In elenco oggi: <span className="font-medium">{corrente.name}</span>
                </div>
                {corrente.fiscal_code ? (
                  <div className="font-mono text-xs">
                    CF: {corrente.fiscal_code}
                    {corrente.cf_cognome_atteso && (
                      <>
                        {"  ·  cognome atteso: "}
                        <span className="font-bold">{corrente.cf_cognome_atteso}</span>
                        {"  ·  nome atteso: "}
                        <span className="font-bold">{corrente.cf_nome_atteso}</span>
                      </>
                    )}
                  </div>
                ) : (
                  <div className="text-xs">Nessun codice fiscale in scheda</div>
                )}
                <div className="text-xs">{motivo(corrente)}</div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="grid gap-2">
                  <Label htmlFor="nr-cognome">Cognome</Label>
                  <Input
                    id="nr-cognome"
                    data-testid="name-review-lastname"
                    className="uppercase"
                    value={cognome}
                    onChange={(e) => setCognome(e.target.value.toUpperCase())}
                  />
                </div>
                <div className="grid gap-2">
                  <Label htmlFor="nr-nome">Nome</Label>
                  <Input
                    id="nr-nome"
                    data-testid="name-review-firstname"
                    className="uppercase"
                    value={nome}
                    onChange={(e) => setNome(e.target.value.toUpperCase())}
                  />
                </div>
              </div>

              <div className="text-xs text-muted-foreground" data-testid="name-review-preview">
                In elenco comparirà: <span className="font-medium">{(nome.trim() + " " + cognome.trim()).trim()}</span>
              </div>

              <div className="flex flex-col sm:flex-row gap-2 sm:justify-end">
                <Button variant="outline" onClick={salta} disabled={inCorso} data-testid="name-review-skip">
                  <SkipForward className="mr-2 h-4 w-4" />
                  Salta
                </Button>
                <Button onClick={conferma} disabled={inCorso} data-testid="name-review-save">
                  <Check className="mr-2 h-4 w-4" />
                  {inCorso ? "Salvo…" : "Conferma e avanti"}
                </Button>
              </div>
            </div>
          ) : (
            <div className="space-y-3">
              <div className="flex items-center gap-2 text-base">
                <Check className="h-5 w-5 text-green-600" />
                <span className="font-medium">
                  {salvati > 0 ? `Fatto: ${salvati} schede sistemate.` : "Nessuna scheda da sistemare."}
                </span>
              </div>
              {saltati > 0 && (
                <p className="text-sm text-muted-foreground">
                  {saltati} {saltati === 1 ? "scheda saltata" : "schede saltate"}: le ritrovi qui la
                  prossima volta.
                </p>
              )}
              <div className="flex justify-end">
                <Button variant="outline" onClick={() => setAperto(false)} data-testid="name-review-close">
                  <X className="mr-2 h-4 w-4" />
                  Chiudi
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
